/**
 * Derived Frontend / Backend tech stack summaries for Product Master CSV and JSON.
 * Same join rules as the spreadsheet enrichment. Not stored on product.yaml.
 */

export const EXPORT_FRONTEND_TECH_STACK_HEADER = 'Frontend tech stack';
export const EXPORT_BACKEND_TECH_STACK_HEADER = 'Backend tech stack';
export const PRODUCT_MASTER_TECH_STACK_PAGE_LIMIT = 150;
const GITHUB_REPO_URL_PATTERN = /github\.com\/([^/\s]+)\/([^/\s?#]+)/i;
const LEADING_VERSION_MAJOR_PATTERN = /^(\d+)/;

export async function collectTechStackPages<T>(
  loadPage: (params: { limit: number; offset: number }) => Promise<{
    repositories?: readonly T[] | null;
    total?: number;
  }>,
): Promise<T[]> {
  const collected: T[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  while (offset < total) {
    const page = await loadPage({
      limit: PRODUCT_MASTER_TECH_STACK_PAGE_LIMIT,
      offset,
    });
    if (!Array.isArray(page?.repositories) || typeof page.total !== 'number') {
      throw new TypeError('tech-stack list is incomplete');
    }
    total = page.total;
    if (page.repositories.length === 0) {
      break;
    }
    collected.push(...page.repositories);
    offset += page.repositories.length;
  }
  return collected;
}

export type TechStackSummaryRepo = {
  repository?: string;
  repository_url?: string;
  product_code?: string;
  language?: string;
  go_version?: string;
  kotlin_version?: string;
  java_version?: string;
  frameworks?: ReadonlyArray<{
    type?: string;
    name?: string;
    version?: string;
  }>;
  nodejs_version?: string;
  typescript_version?: string;
  frontend_framework?: string;
  frontend_library?: string;
};

export type ProductMasterTechStackInventory = {
  frontend: readonly TechStackSummaryRepo[];
  backend: readonly TechStackSummaryRepo[];
};

export type ProductMasterTechStackSummaries = {
  frontend: string;
  backend: string;
};

type RepoId = { orgRepo: string; bare: string; hasOrg: boolean };

type RepoIndex = {
  byOrgRepo: Map<string, TechStackSummaryRepo>;
  byBare: Map<string, TechStackSummaryRepo[]>;
  byProduct: Map<string, TechStackSummaryRepo[]>;
};

function textFromFiniteNumber(value: number): string {
  return Number.isFinite(value) ? value.toString() : '';
}

function cleanText(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number') {
    return textFromFiniteNumber(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return '';
}

function stripGitSuffix(value: string): string {
  return value.toLowerCase().endsWith('.git') ? value.slice(0, -4) : value;
}

function stripTrailingSlashes(value: string): string {
  let end = value.length;
  while (end > 0 && value.charAt(end - 1) === '/') {
    end -= 1;
  }
  return value.slice(0, end);
}

export function normalizeTechStackRepoId(value: unknown): RepoId {
  const text = stripTrailingSlashes(stripGitSuffix(cleanText(value)));
  if (!text) {
    return { orgRepo: '', bare: '', hasOrg: false };
  }
  const url = GITHUB_REPO_URL_PATTERN.exec(text);
  if (url) {
    const org = url[1].toLowerCase();
    const repo = stripGitSuffix(url[2]).toLowerCase();
    return { orgRepo: `${org}/${repo}`, bare: repo, hasOrg: true };
  }
  const parts = text.split('/').filter(Boolean);
  if (parts.length >= 2) {
    const org = parts[parts.length - 2].toLowerCase();
    const repo = stripGitSuffix(parts[parts.length - 1]).toLowerCase();
    return { orgRepo: `${org}/${repo}`, bare: repo, hasOrg: true };
  }
  return { orgRepo: '', bare: text.toLowerCase(), hasOrg: false };
}

function techStackRepoId(repo: TechStackSummaryRepo): string {
  const fromUrl = normalizeTechStackRepoId(repo.repository_url);
  if (fromUrl.orgRepo) {
    return fromUrl.orgRepo;
  }
  return cleanText(repo.repository).toLowerCase();
}

function indexTechStackRepos(repos: readonly TechStackSummaryRepo[]): RepoIndex {
  const byOrgRepo = new Map<string, TechStackSummaryRepo>();
  const byBare = new Map<string, TechStackSummaryRepo[]>();
  const byProduct = new Map<string, TechStackSummaryRepo[]>();
  for (const repo of repos) {
    if (!repo || typeof repo !== 'object') {
      continue;
    }
    const fromUrl = normalizeTechStackRepoId(repo.repository_url);
    const bare = cleanText(repo.repository).toLowerCase() || fromUrl.bare;
    if (fromUrl.orgRepo && !byOrgRepo.has(fromUrl.orgRepo)) {
      byOrgRepo.set(fromUrl.orgRepo, repo);
    }
    if (bare) {
      const list = byBare.get(bare) ?? [];
      list.push(repo);
      byBare.set(bare, list);
    }
    const code = cleanText(repo.product_code).toLowerCase();
    if (code && code !== 'not_found') {
      const list = byProduct.get(code) ?? [];
      list.push(repo);
      byProduct.set(code, list);
    }
  }
  return { byOrgRepo, byBare, byProduct };
}

function lookupTechStackRepo(ref: RepoId, index: RepoIndex): TechStackSummaryRepo | null {
  if (ref.hasOrg && ref.orgRepo) {
    return index.byOrgRepo.get(ref.orgRepo) ?? null;
  }
  if (!ref.hasOrg && ref.bare) {
    const hits = index.byBare.get(ref.bare) ?? [];
    if (hits.length === 1) {
      return hits[0];
    }
  }
  return null;
}

function formatNodeRuntime(value: unknown): string {
  const raw = cleanText(value);
  if (!raw) {
    return '';
  }
  if (/^node\b/i.test(raw)) {
    return raw;
  }
  const major = LEADING_VERSION_MAJOR_PATTERN.exec(raw.replace(/^v/i, ''));
  return major ? `Node ${major[1]}` : `Node ${raw}`;
}

function formatFrameworkToken(
  item:
    | {
        name?: string;
        version?: string;
      }
    | null
    | undefined,
): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const name = cleanText(item.name);
  const version = cleanText(item.version);
  if (!name) {
    return '';
  }
  if (!version || name.includes('@') || name.includes(version)) {
    return name;
  }
  return `${name}@${version}`;
}

function pickBackendFramework(
  frameworks: TechStackSummaryRepo['frameworks'],
): string {
  if (!Array.isArray(frameworks)) {
    return '';
  }
  let fallback = '';
  for (const item of frameworks) {
    const token = formatFrameworkToken(item);
    if (!token) {
      continue;
    }
    if (!fallback) {
      fallback = token;
    }
    if (cleanText(item?.type).toLowerCase() === 'web') {
      return token;
    }
  }
  return fallback;
}

export function summarizeBackendTechStack(repo: TechStackSummaryRepo | null | undefined): string {
  if (!repo) {
    return '';
  }
  const language = cleanText(repo.language);
  const runtime =
    cleanText(repo.go_version) ||
    cleanText(repo.kotlin_version) ||
    cleanText(repo.java_version);
  const framework = pickBackendFramework(repo.frameworks);
  const langPart = [language, runtime].filter(Boolean).join(' ');
  if (langPart && framework) {
    return `${langPart} / ${framework}`;
  }
  return langPart || framework;
}

export function summarizeFrontendTechStack(repo: TechStackSummaryRepo | null | undefined): string {
  if (!repo) {
    return '';
  }
  const language = cleanText(repo.language);
  const runtime = cleanText(repo.nodejs_version)
    ? formatNodeRuntime(repo.nodejs_version)
    : cleanText(repo.typescript_version);
  const framework =
    cleanText(repo.frontend_framework) || cleanText(repo.frontend_library);
  const langPart = [language, runtime].filter(Boolean).join(' ');
  if (langPart && framework) {
    return `${langPart} / ${framework}`;
  }
  return langPart || framework;
}

function joinTechStackForSide(
  productCode: string,
  mappings: readonly string[],
  index: RepoIndex,
  summarize: (repo: TechStackSummaryRepo) => string,
): string {
  const matches: TechStackSummaryRepo[] = [];
  const seenRepo = new Set<string>();
  let githubHit = false;
  for (const raw of mappings) {
    const repo = lookupTechStackRepo(normalizeTechStackRepoId(raw), index);
    if (!repo) {
      continue;
    }
    githubHit = true;
    const id = techStackRepoId(repo);
    if (!id || seenRepo.has(id)) {
      continue;
    }
    seenRepo.add(id);
    matches.push(repo);
  }
  if (!githubHit) {
    const code = cleanText(productCode).toLowerCase();
    if (code && code !== 'not_found') {
      for (const repo of index.byProduct.get(code) ?? []) {
        const id = techStackRepoId(repo);
        if (!id || seenRepo.has(id)) {
          continue;
        }
        seenRepo.add(id);
        matches.push(repo);
      }
    }
  }
  const tokens: string[] = [];
  const seenToken = new Set<string>();
  for (const repo of matches) {
    const token = summarize(repo);
    if (!token || seenToken.has(token)) {
      continue;
    }
    seenToken.add(token);
    tokens.push(token);
  }
  return tokens.join('; ');
}

export function techStackSummariesForMappings(
  productCode: string,
  githubFrontend: readonly string[],
  githubBackend: readonly string[],
  inventory: ProductMasterTechStackInventory,
): ProductMasterTechStackSummaries {
  const frontendIndex = indexTechStackRepos(inventory.frontend);
  const backendIndex = indexTechStackRepos(inventory.backend);
  return {
    frontend: joinTechStackForSide(
      productCode,
      githubFrontend,
      frontendIndex,
      summarizeFrontendTechStack,
    ),
    backend: joinTechStackForSide(
      productCode,
      githubBackend,
      backendIndex,
      summarizeBackendTechStack,
    ),
  };
}
